import React, { useState, useEffect, useCallback } from 'react';
import { Search, Receipt, Download, RefreshCw } from 'lucide-react';
import { invoiceService, type InvoiceMetadata } from '../services/invoiceService';
import { useAuth } from '../contexts/AuthContext';

const InvoiceDashboard: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  
  // Void invoice modal state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceMetadata | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      // Load all invoices from Storage
      console.log('Loading invoices...');
      const fetchedInvoices = await invoiceService.listInvoices();
      console.log(`✅ Loaded ${fetchedInvoices.length} invoices`);
      setInvoices(fetchedInvoices);
    } catch (error) {
      console.error('❌ Error loading invoices:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message, error.stack);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncInvoices = async () => {
    if (!user) return;
    
    setSyncing(true);
    try {
      console.log('Manually syncing invoices for completed quotes...');
      const createdCount = await invoiceService.createInvoicesForCompletedQuotes(
        user.uid,
        user.displayName || 'System'
      );
      
      if (createdCount > 0) {
        console.log(`✅ Created ${createdCount} invoices`);
        alert(`${createdCount} fehlende Rechnungen wurden erstellt.`);
        // Reload invoices
        await loadInvoices();
      } else {
        console.log('No new invoices needed');
        alert('Alle abgeschlossenen Angebote haben bereits Rechnungen.');
      }
    } catch (error) {
      console.error('❌ Error syncing invoices:', error);
      alert('Fehler beim Erstellen der Rechnungen. Siehe Console für Details.');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleDownload = (invoice: InvoiceMetadata) => {
    if (invoice.downloadURL) {
      window.open(invoice.downloadURL, '_blank');
    }
  };

  const handleVoidClick = (invoice: InvoiceMetadata) => {
    setSelectedInvoice(invoice);
    setShowVoidModal(true);
  };

  const handleVoidInvoice = async () => {
    if (!voidReason.trim()) {
      alert('Bitte geben Sie einen Grund ein');
      return;
    }

    if (!user || !selectedInvoice) {
      alert('Benutzer nicht gefunden');
      return;
    }

    setIsVoiding(true);
    try {
      await invoiceService.voidInvoice(
        selectedInvoice.invoiceNumber,
        voidReason,
        user.uid,
        user.displayName || 'Admin'
      );
      alert('Rechnung erfolgreich storniert');
      loadInvoices();
      setShowVoidModal(false);
      setVoidReason('');
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error voiding invoice:', error);
      alert('Fehler beim Stornieren');
    } finally {
      setIsVoiding(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesSearch = 
        invoice.clientName.toLowerCase().includes(search) ||
        invoice.projectName?.toLowerCase().includes(search) ||
        invoice.invoiceNumber.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Rechnungen</h1>
        <button
          onClick={handleSyncInvoices}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={syncing ? 'animate-spin' : ''} size={18} />
          {syncing ? 'Erstelle Rechnungen...' : 'Fehlende Rechnungen erstellen'}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Suchen (Kunde, Rechnungsnummer, Projekt)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">Keine Rechnungen gefunden</p>
            <p className="text-sm">
              {invoices.length === 0 
                ? 'Rechnungen werden automatisch erstellt, sobald ein Angebot als "Abgeschlossen" markiert wird.'
                : 'Keine Rechnungen entsprechen den Filterkriterien.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rechnungsnummer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kunde
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projekt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Betrag
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktion
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvoices.map((invoice) => (
                  <tr 
                    key={invoice.invoiceNumber} 
                    className={`hover:bg-gray-50 transition-colors ${invoice.status === 'void' ? 'opacity-60' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">
                      <div className="flex items-center gap-2">
                        <span className={invoice.status === 'void' ? 'line-through text-gray-400' : 'text-gray-900'}>
                          {invoice.invoiceNumber}
                        </span>
                        {invoice.status === 'void' && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                            Storniert
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${invoice.status === 'void' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      {invoice.clientName}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${invoice.status === 'void' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      {invoice.projectName || '-'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${invoice.status === 'void' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                      €{invoice.amountTotal.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.createdAt.toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(invoice)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                        >
                          <Download size={16} />
                          Download
                        </button>
                        {invoice.status !== 'void' && (
                          <button
                            onClick={() => handleVoidClick(invoice)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-800 text-sm"
                          >
                            Stornieren
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Void Invoice Modal */}
      {showVoidModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Rechnung stornieren</h3>
            <p className="text-gray-600 mb-2">
              Rechnung: <span className="font-mono font-semibold">{selectedInvoice.invoiceNumber}</span>
            </p>
            <p className="text-gray-600 mb-4">
              Bitte geben Sie einen Grund für die Stornierung an:
            </p>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="w-full border rounded-lg p-2 mb-4 min-h-[100px]"
              rows={3}
              placeholder="Grund für Stornierung..."
            />
            <div className="flex gap-2 justify-end">
              <button 
                onClick={() => {
                  setShowVoidModal(false);
                  setVoidReason('');
                  setSelectedInvoice(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleVoidInvoice}
                disabled={isVoiding || !voidReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isVoiding ? 'Storniere...' : 'Stornieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDashboard;

